import type { GraphApi } from './types'
import { isActiveGraphApi } from './active-context'
import {
  assertAutomationTurnCurrent,
  currentGraphApiInvocation,
  noteManualGraphInteraction,
} from '@/features/backtest/hooks/graph-automation'

export const GRAPH_READ_METHODS = [
  'help',
  'ls',
  'listNodeTypes',
  'getGraph',
  'getNode',
  'listDebugPanes',
  'findDanglingNodes',
  'validateGraph',
  'listGroups',
  'getGroup',
  'getLayout',
  'getStrategyInfo',
  'listStrategies',
  'getAutoRun',
  'getRunState',
  'getRunResult',
  'getTransactions',
  'getDebugData',
] as const

export const GRAPH_MUTATION_METHODS = [
  'addNode',
  'setParams',
  'connect',
  'setStaticInput',
  'setInputField',
  'removeNode',
  'clearGraph',
  'attachDebugChart',
  'movePaneToMain',
  'movePaneToOwnPane',
  'focusOnPrice',
  'unfocusFromPrice',
  'tidy',
  'organizeNodes',
  'fitView',
  'focusNode',
  'groupNodes',
  'setGroupCollapsed',
  'dissolveGroup',
  'exposeParam',
  'unexposeParam',
  'setLayout',
  'renameStrategy',
  'saveStrategy',
  'newStrategy',
  'loadStrategy',
  'duplicateStrategy',
  'setAutoRun',
  'run',
] as const

export const GRAPH_METHODS = [...GRAPH_READ_METHODS, ...GRAPH_MUTATION_METHODS] as const
export type GraphMethod = (typeof GRAPH_METHODS)[number]

export type GraphMethodValidation =
  | { ok: true; method: GraphMethod }
  | {
      ok: false
      code: 'GRAPH_METHOD_NOT_ALLOWED' | 'GRAPH_ARGS_INVALID'
      message: string
    }

export interface GraphApiValidationOptions {
  canWrite?: () => boolean
  isContextActive?: () => boolean
}

const readMethods = new Set<string>(GRAPH_READ_METHODS)
const mutationMethods = new Set<string>(GRAPH_MUTATION_METHODS)
const allMethods = new Set<string>(GRAPH_METHODS)

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const isString = (value: unknown): value is string => typeof value === 'string'
const isBoolean = (value: unknown): value is boolean => typeof value === 'boolean'
const isPrimitive = (value: unknown): value is string | number | boolean =>
  isString(value) || (typeof value === 'number' && Number.isFinite(value)) || isBoolean(value)

function exactArgs(args: unknown[], count: number): string | null {
  return args.length === count ? null : `expects ${count} argument(s).`
}

function stringArg(args: unknown[], index: number): string | null {
  return isString(args[index]) && args[index].length > 0
    ? null
    : `argument ${index + 1} must be a string.`
}

function optionalRecord(args: unknown[], index: number): string | null {
  return args[index] === undefined || isRecord(args[index])
    ? null
    : `argument ${index + 1} must be an object.`
}

function validateMethodArgs(method: GraphMethod, args: unknown[]): string | null {
  let error: string | null

  switch (method) {
    case 'help':
      error =
        args.length <= 1 && (args.length === 0 || isString(args[0]))
          ? null
          : 'expects an optional string node type.'
      break
    case 'getNode':
    case 'removeNode':
    case 'focusNode':
    case 'getGroup':
    case 'dissolveGroup':
    case 'renameStrategy':
    case 'loadStrategy':
      error = exactArgs(args, 1) ?? stringArg(args, 0)
      break
    case 'getGraph':
    case 'ls':
    case 'listNodeTypes':
    case 'listDebugPanes':
    case 'findDanglingNodes':
    case 'validateGraph':
    case 'listGroups':
    case 'getLayout':
    case 'getStrategyInfo':
    case 'listStrategies':
    case 'getAutoRun':
    case 'getRunState':
    case 'getRunResult':
    case 'getTransactions':
    case 'saveStrategy':
    case 'duplicateStrategy':
    case 'clearGraph':
    case 'tidy':
    case 'organizeNodes':
    case 'fitView':
    case 'run':
      error = exactArgs(args, 0)
      break
    case 'getDebugData':
      error =
        args.length <= 1 && (args.length === 0 || isRecord(args[0]))
          ? null
          : 'expects an optional object.'
      if (!error && isRecord(args[0]) && args[0].full !== undefined && !isBoolean(args[0].full)) {
        error = 'option full must be a boolean.'
      }
      break
    case 'addNode':
      error =
        args.length >= 1 && args.length <= 2 ? stringArg(args, 0) : 'expects 1 or 2 argument(s).'
      if (!error) error = optionalRecord(args, 1)
      if (!error && isRecord(args[1]) && args[1].position !== undefined) {
        const position = args[1].position
        error =
          isRecord(position) &&
          typeof position.x === 'number' &&
          Number.isFinite(position.x) &&
          typeof position.y === 'number' &&
          Number.isFinite(position.y)
            ? null
            : 'option position must contain finite x and y numbers.'
      }
      if (
        !error &&
        isRecord(args[1]) &&
        args[1].params !== undefined &&
        !isRecord(args[1].params)
      ) {
        error = 'option params must be an object.'
      }
      break
    case 'setParams':
      error = exactArgs(args, 2) ?? stringArg(args, 0)
      if (!error && !isRecord(args[1])) error = 'argument 2 must be an object.'
      break
    case 'connect':
      error = exactArgs(args, 4)
      if (!error) {
        for (let i = 0; i < 4; i += 1) {
          error = stringArg(args, i)
          if (error) break
        }
      }
      break
    case 'setStaticInput':
      error = exactArgs(args, 3) ?? stringArg(args, 0) ?? stringArg(args, 1)
      if (!error && !isPrimitive(args[2]))
        error = 'argument 3 must be a string, number, or boolean.'
      break
    case 'setInputField':
      error = exactArgs(args, 3) ?? stringArg(args, 0) ?? stringArg(args, 1) ?? stringArg(args, 2)
      break
    case 'attachDebugChart':
      error =
        args.length >= 1 && args.length <= 3 ? stringArg(args, 0) : 'expects 1 to 3 argument(s).'
      if (!error && args[1] !== undefined) error = stringArg(args, 1)
      if (!error && args[2] !== undefined && args[2] !== 'separate' && args[2] !== 'overlay') {
        error = 'argument 3 must be separate or overlay.'
      }
      break
    case 'movePaneToMain':
    case 'movePaneToOwnPane':
    case 'focusOnPrice':
    case 'unfocusFromPrice':
      error = exactArgs(args, 1) ?? stringArg(args, 0)
      break
    case 'groupNodes':
      error =
        args.length >= 1 && args.length <= 2
          ? Array.isArray(args[0]) && args[0].every(isString)
            ? null
            : 'argument 1 must be an array of strings.'
          : 'expects 1 or 2 argument(s).'
      if (!error) error = optionalRecord(args, 1)
      if (!error && isRecord(args[1]) && args[1].label !== undefined && !isString(args[1].label)) {
        error = 'option label must be a string.'
      }
      break
    case 'setGroupCollapsed':
      error = exactArgs(args, 2) ?? stringArg(args, 0)
      if (!error && !isBoolean(args[1])) error = 'argument 2 must be a boolean.'
      break
    case 'exposeParam':
      error =
        args.length >= 2 && args.length <= 3
          ? (stringArg(args, 0) ?? stringArg(args, 1))
          : 'expects 2 or 3 argument(s).'
      if (!error && args[2] !== undefined) error = stringArg(args, 2)
      break
    case 'unexposeParam':
      error = exactArgs(args, 2) ?? stringArg(args, 0) ?? stringArg(args, 1)
      break
    case 'setLayout':
      error = exactArgs(args, 1) ?? (isRecord(args[0]) ? null : 'argument 1 must be an object.')
      if (!error && isRecord(args[0])) {
        const opts = args[0]
        if (
          opts.chartPosition !== undefined &&
          opts.chartPosition !== 'top' &&
          opts.chartPosition !== 'right'
        ) {
          error = 'chartPosition must be top or right.'
        }
        if (
          !error &&
          opts.panelPosition !== undefined &&
          opts.panelPosition !== 'right' &&
          opts.panelPosition !== 'floating'
        ) {
          error = 'panelPosition must be right or floating.'
        }
        if (
          !error &&
          opts.panes !== undefined &&
          (!Array.isArray(opts.panes) ||
            !opts.panes.every((pane) => pane === 'editor' || pane === 'chart'))
        ) {
          error = 'panes must contain only editor or chart.'
        }
        if (!error && opts.grid !== undefined) {
          const grid = opts.grid
          if (
            !isRecord(grid) ||
            ![1, 2, 3].includes(grid.rows as number) ||
            ![1, 2, 3].includes(grid.cols as number)
          ) {
            error = 'grid rows and cols must be 1, 2, or 3.'
          }
        }
      }
      break
    case 'newStrategy':
      error =
        args.length <= 1 && (args.length === 0 || isString(args[0]))
          ? null
          : 'expects an optional string name.'
      break
    case 'setAutoRun':
      error = exactArgs(args, 1) ?? (isBoolean(args[0]) ? null : 'argument 1 must be a boolean.')
      break
  }

  return error
}

export function validateGraphMethodCall(method: string, args: unknown[]): GraphMethodValidation {
  if (!allMethods.has(method) || (!readMethods.has(method) && !mutationMethods.has(method))) {
    return {
      ok: false,
      code: 'GRAPH_METHOD_NOT_ALLOWED',
      message: `Unsupported graph method: ${method}.`,
    }
  }
  const error = validateMethodArgs(method as GraphMethod, args)
  return error
    ? { ok: false, code: 'GRAPH_ARGS_INVALID', message: `${method} ${error}` }
    : { ok: true, method: method as GraphMethod }
}

export function dispatchGraphMethod(
  api: GraphApi | Record<string, unknown>,
  method: string,
  args: unknown[],
): unknown {
  const validation = validateGraphMethodCall(method, args)
  if (!validation.ok) {
    const error = new TypeError(validation.message)
    Object.assign(error, { code: validation.code })
    throw error
  }
  const candidate = api[validation.method]
  if (typeof candidate !== 'function') {
    const error = new TypeError(`Graph API method is unavailable: ${validation.method}.`)
    Object.assign(error, { code: 'GRAPH_METHOD_NOT_ALLOWED' })
    throw error
  }
  return Reflect.apply(candidate, api, args)
}

export function createValidatedGraphApi(
  api: GraphApi,
  options: GraphApiValidationOptions = {},
): GraphApi {
  const exposedApi = new Proxy(api, {
    get(target, property, receiver) {
      const value = Reflect.get(target, property, receiver)
      if (
        typeof property !== 'string' ||
        !allMethods.has(property) ||
        typeof value !== 'function'
      ) {
        return value
      }
      return (...args: unknown[]) => {
        if (!isActiveGraphApi(exposedApi)) {
          const error = new Error('The strategy editor context is no longer active.')
          Object.assign(error, { code: 'GRAPH_CONTEXT_STALE' })
          throw error
        }
        if (options.isContextActive && !options.isContextActive()) {
          const error = new Error('The strategy editor context is no longer active.')
          Object.assign(error, { code: 'GRAPH_CONTEXT_STALE' })
          throw error
        }
        if (mutationMethods.has(property) && options.canWrite && !options.canWrite()) {
          const error = new Error('The strategy editor is read-only.')
          Object.assign(error, { code: 'GRAPH_READ_ONLY' })
          throw error
        }
        if (mutationMethods.has(property)) {
          const invocation = currentGraphApiInvocation()
          if (invocation) {
            assertAutomationTurnCurrent(invocation.turn, invocation.manualEpoch)
          } else {
            noteManualGraphInteraction()
          }
        }
        return dispatchGraphMethod(target, property, args)
      }
    },
  })
  return exposedApi
}
